// import BaseApi from 'shared/api/BaseApi'
import { BaseHttpService } from 'services/api/BaseHttpService'
import { QueryTypes } from 'services/api/data'

import { newBCraftServicesUrl } from 'shared/constants'
import { externalGamesFolderByExternalGameType } from 'shared/constants/slots'

import {
  IProducer,
  IProducersList,
  IServerFavorite,
  IServerResponse,
  ServerGameType,
} from 'modules/SlotsRoot/types/interfaces/Api'
import {
  INewGamesDTO,
  ISlotViewItem,
  ITopSlotsDTO,
  IWagerGamesDTO,
} from 'modules/SlotsRoot/types/interfaces/Slots/Slots'

import { slotProvidersConfiguration } from './configuration'
import FavoriteConverter from './providersData/Favorite/Favorite'
import GameCatalogConverter from './providersData/GameCatalog/GameCatalogConverter'
import { ProvidersType } from './types/configuration'
import { LoadDigitainSlotTokenData } from './types/interfaces/Slots/DigitainSlot'
import { IFavoriteDTO } from './types/interfaces/Slots/Favorite'
import { IGetGameUrl } from './types/slot'

export interface IGetGameList {
  id: string
  provider: string
  extraData?: unknown
  platform?: 'desktop' | 'mobile'
}

export interface IGetExtraData {
  url: string
  converter?: (data: unknown) => unknown
}

export interface IGetSessionUrl {
  provider: ProvidersType
  gameId: string | number
  hasFreeSpins?: boolean
  params: object
  extraData: object
  gameList: ISlotViewItem[]
  demoStart?: boolean
  isFreeSpins?: boolean
}

export class SlotsApi extends BaseHttpService {
  private favoriteConverter

  private gamecatalogConverter

  private baseFrontendServiceUrl

  constructor(baseUrl: string) {
    super(baseUrl)
    this.baseUrl = baseUrl
    this.favoriteConverter = new FavoriteConverter()
    this.gamecatalogConverter = new GameCatalogConverter()
    this.baseFrontendServiceUrl = `${baseUrl}/frontend-service`
  }

  async getGameList({ provider, extraData, platform }: IGetGameList) {
    const { gameListUrl, converter } = slotProvidersConfiguration[provider]

    if (gameListUrl.includes('https://')) {
      return this.sendQuery<ServerGameType, ISlotViewItem[]>({
        type: QueryTypes.GET,
        url: `${gameListUrl}`,
        converterSuccess: data => converter.convertGameList({ data, extraData, platform }),
      })
    }

    return this.sendQuery<ServerGameType, ISlotViewItem[]>({
      type: QueryTypes.GET,
      url: `${this.baseUrl}/${gameListUrl}`,
      converterSuccess: data => converter.convertGameList({ data, extraData, platform }),
    })
  }

  async getFavoriteGameList() {
    return this.sendQuery<IFavoriteDTO[], string[]>({
      url: `${this.baseUrl}/api/users/favouritegames/`,
      type: QueryTypes.GET,
      converterSuccess: this.favoriteConverter.convertGameList,
    })
  }

  async getExtraData({ url, converter }: IGetExtraData): Promise<IServerResponse<unknown>> {
    return this.sendQuery({ type: QueryTypes.GET, url: `${this.baseUrl}/${url}`, converterSuccess: converter })
  }

  async getSessionUrl({
    provider,
    gameId,
    params = {},
    extraData = {},
    gameList,
    demoStart,
    isFreeSpins,
  }: IGetSessionUrl): Promise<IServerResponse<string>> {
    const {
      sessionUrlData: { url, converter, getBody, getQuery, method, demoUrl, freeSpinUrl, isNeedDoubleRequest } = {},
    } = slotProvidersConfiguration[provider]

    const body = getBody ? getBody({ gameId, ...params, gameList }) : undefined
    const urlQuery = getQuery ? getQuery({ gameId, ...params }) : ''
    const activeUrl = (demoStart && demoUrl) || (isFreeSpins && freeSpinUrl) || url

    if (activeUrl) {
      let sessionRes = await this.sendQuery({
        type: method || QueryTypes.POST,
        url: `${this.baseUrl}/${activeUrl}${urlQuery}`,
        data: body,
        // options: { headers }, // TODO
        converterSuccess: (data: any) =>
          converter
            ? converter({ data, extraData, gameId, gameList, lang: SlotsApi.lang } as IGetGameUrl<unknown, unknown>)
            : data,
      })

      if (isNeedDoubleRequest) {
        try {
          const extraSessionResponse = await fetch(sessionRes.data)

          if (extraSessionResponse.ok) {
            const sessionData: { data: string } | string = await extraSessionResponse.json()

            sessionRes = {
              success: true,
              data: (typeof sessionData !== 'string' && sessionData?.data) || sessionData,
              errorMessage: '',
              codeStatus: '',
            }
          }
        } catch (error) {
          throw new Error(`get extra session error: ${error}`)
        }
      }

      return sessionRes
    }

    return new Promise(resolve => {
      resolve({
        success: true,
        data: converter
          ? converter({ data: {}, extraData, gameId, gameList, lang: SlotsApi.lang } as IGetGameUrl<unknown, unknown>)
          : '',
        errorMessage: '',
        codeStatus: '',
      })
    })
  }

  async getFreeSpinInfo(provider: ProvidersType) {
    const { sessionUrlData: { freeSpinInfoUrl, freeSpinInfoConverter } = {} } = slotProvidersConfiguration[provider]
    return this.sendQuery({
      type: QueryTypes.GET,
      url: `${this.baseUrl}/${freeSpinInfoUrl}`,
      converterSuccess: freeSpinInfoConverter,
    })
  }

  async addSlot(provider: string, gameId: string) {
    return this.sendQuery<IServerFavorite, boolean>({
      type: QueryTypes.POST,
      url: `${this.baseUrl}/api/users/favouritegames/`,
      data: {
        provider,
        game_id: gameId,
      },
    })
  }

  async removeSlot(provider: string, gameId: string) {
    return this.sendQuery<IServerFavorite, boolean>({
      type: QueryTypes.DELETE,
      url: `${this.baseUrl}/api/users/favouritegames/`,
      data: {
        data: {
          provider,
          game_id: gameId,
        },
      },
    })
  }

  async loadProducers() {
    return this.sendQuery<IProducersList, IProducer[]>({
      type: QueryTypes.GET,
      url: `${this.baseUrl}/api/integrations/game-catalog/producers/`,
      converterSuccess: this.gamecatalogConverter.convertProducers,
    })
  }

  async getTopSlotsList(subDirectory: string) {
    return this.sendQuery<ITopSlotsDTO, string[]>({
      type: QueryTypes.GET,
      // eslint-disable-next-line max-len
      url: `${this.baseFrontendServiceUrl}/game-list/top/${subDirectory}/${externalGamesFolderByExternalGameType.top}`,
      converterSuccess: data => this.gamecatalogConverter.convertTopSlots(data),
    })
  }

  async getNewGamesList(subDirectory: string) {
    return this.sendQuery<INewGamesDTO, string[]>({
      type: QueryTypes.GET,
      // eslint-disable-next-line max-len
      url: `${this.baseFrontendServiceUrl}/game-list/new/${subDirectory}/${externalGamesFolderByExternalGameType.new}`,
      converterSuccess: data => this.gamecatalogConverter.convertNewGames(data),
    })
  }

  async getWagerSlotsList(subDirectory: string) {
    return this.sendQuery<IWagerGamesDTO, string[]>({
      type: QueryTypes.GET,
      // eslint-disable-next-line max-len
      url: `${this.baseFrontendServiceUrl}/game-list/wager/${subDirectory}/${externalGamesFolderByExternalGameType.wager}`,
      converterSuccess: data => this.gamecatalogConverter.convertWagerSlots(data),
    })
  }

  async getGamesCategoriesList() {
    return this.sendQuery<unknown, unknown>({
      type: QueryTypes.GET,
      // eslint-disable-next-line max-len
      url: `${this.baseFrontendServiceUrl}/game-list/categories/main/${externalGamesFolderByExternalGameType.categories}`,
    })
  }

  async loadDigitainSlotToken(data: LoadDigitainSlotTokenData) {
    return this.sendQuery({
      type: QueryTypes.POST,
      url: `${this.baseUrl}/api/integrations/81339664/token/`,
      data,
      converterSuccess: this.gamecatalogConverter.convertDigitainSlotToken,
    })
  }
}
